<script lang="ts" setup>
import { AppButton, AppPassword, AppPasswordCriteria } from '#core/components';
import type { FormErrors } from '#core/types/Form';
import { useVuelidate } from '@vuelidate/core';
import { computed, ref } from 'vue';
import type { ForgotPasswordResetPasswordForm } from '../../types';

const { username = '', token = '' } = defineProps<{
  username: string;
  token: string;
}>();

const { validations } = useValidation();

const showPassword = ref(false);
const showPasswordConfirmation = ref(false);

const form = ref({
  password: '',
  password_confirmation: '',
});

const rules = computed(() => ({
  password: {
    required: validations.required('Password'),
    minLength: validations.minLength('Password', 8),
    password: validations.password(),
  },
  password_confirmation: {
    required: validations.required('Password Confirmation'),
    sameAs: validations.sameAs('Password Confirmation', 'Password', form.value.password),
  },
}));

const router = useRouter();

const v$ = useVuelidate(rules, form, { $autoDirty: true });

const {
  pending: loadingResetPassword,
  execute: execResetPassword,
  error: serverError,
} = useSubmit<{}, { data: FormErrors<ForgotPasswordResetPasswordForm> }>();

const handleSignUp = async (): Promise<void> => {
  const result = await v$.value.$validate();
  if (!result) return;

  await execResetPassword('/auth/reset-password', {
    method: 'POST',
    body: { ...form.value, username, token },
  });

  if (!serverError.value) {
    router.push({ name: 'login' });
  }
};

const errors = computed(() => {
  return $parsedError(form.value, v$, serverError.value?.data?.errors || {});
});
</script>

<template>
  <v-form @submit.prevent="handleSignUp" class="d-flex flex-column gap-xl">
    <AppPassword
      v-model="form.password"
      label="Password"
      :disabled="loadingResetPassword"
      :error-messages="errors.password"
      data-testid="password"
      data-cy="input-password"
      autofocus
    >
    </AppPassword>

    <div class="d-flex flex-column gap-lg">
      <AppPassword
        v-model="form.password_confirmation"
        label="Confirm Password"
        :disabled="loadingResetPassword"
        :error-messages="errors.password_confirmation"
        data-testid="password-confirmation"
        data-cy="input-password-confirmation"
      >
      </AppPassword>

      <AppPasswordCriteria :password="form.password" />
    </div>

    <AppButton
      type="submit"
      data-testid="continue"
      block
      class="font-weight-bold w-full"
      :loading="loadingResetPassword"
    >
      Confirm New Password
    </AppButton>
  </v-form>
</template>

<style lang="scss" scoped>
.v-card {
  margin-top: 132px;
}
</style>
